import { useQuery } from '@tanstack/react-query';
import { Brain, Server } from 'lucide-react';

import { StatusBadge } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { diagnosticsApi, diagnosticsKeys } from '@/features/diagnostics/api';
import type { ApiError } from '@/lib/errors';

export function DiagnosticsPage(): JSX.Element {
  const controlPlane = useQuery({
    queryKey: diagnosticsKeys.controlPlane,
    queryFn: diagnosticsApi.controlPlaneHealth,
  });

  const ai = useQuery({
    queryKey: diagnosticsKeys.ai,
    queryFn: diagnosticsApi.aiHealth,
  });

  return (
    <div className="space-y-7">
      <div>
        <div className="eyebrow eyebrow-dot mb-2.5">
          <span>ADMIN</span>
        </div>
        <h1 className="h-display">Diagnostics</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-500 dark:text-ink-400">
          Live connectivity across the dual-engine stack. The AI check exercises the signed Laravel
          → Python handshake end-to-end.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <section className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 shadow-soft animate-slide-up">
          <header className="flex items-start gap-3 border-b border-ink-100 dark:border-white/5 px-6 py-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-sky/10 text-accent-sky">
              <Server className="h-4 w-4" />
            </div>
            <div>
              <h3 className="h-section">Control plane</h3>
              <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">Laravel REST API</p>
            </div>
          </header>
          <div className="p-6">
            {controlPlane.isPending ? (
              <Spinner />
            ) : controlPlane.isError ? (
              <ErrorState error={controlPlane.error as ApiError} />
            ) : (
              <StatusBadge
                ok
                label={`${controlPlane.data.service} · ${controlPlane.data.status}`}
              />
            )}
          </div>
        </section>

        <section className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 shadow-soft animate-slide-up">
          <header className="flex items-start gap-3 border-b border-ink-100 dark:border-white/5 px-6 py-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-violet/10 text-accent-violet">
              <Brain className="h-4 w-4" />
            </div>
            <div>
              <h3 className="h-section">AI service</h3>
              <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">FastAPI · via control plane</p>
            </div>
          </header>
          <div className="p-6">
            {ai.isPending ? (
              <Spinner />
            ) : ai.isError ? (
              <ErrorState error={ai.error as ApiError} />
            ) : (
              <div className="space-y-3">
                <StatusBadge
                  ok={ai.data.ai_service.status === 'ok'}
                  label={ai.data.ai_service.status}
                />
                <dl className="overflow-hidden rounded-lg border border-ink-200/70 dark:border-white/10 text-xs">
                  {[
                    ['Version', ai.data.ai_service.version],
                    ['LLM provider', ai.data.ai_service.llm_provider],
                    ['Vector store', ai.data.ai_service.vector_store],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-center justify-between border-b border-ink-100 dark:border-white/5 px-3 py-2 last:border-b-0"
                    >
                      <dt className="font-mono text-[10px] tracking-[0.18em] text-ink-500 dark:text-ink-400">
                        {k?.toUpperCase()}
                      </dt>
                      <dd className="font-mono text-xs text-ink-800 dark:text-ink-100">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
